import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { DemoGenieService } from "@/services/demo/DemoGenieService";
import { Agent } from "@/types/agent/agent";

interface UseDemoConversationReturn {
  /** Start a demo for a persona. Handles lazy provisioning. Returns the ElevenLabs agent ID. */
  startDemo: (personaId: string) => Promise<string>;
  /** Whether a demo is currently being provisioned */
  isProvisioning: boolean;
  /** Provisioning error if any */
  provisionError: string | null;
  /** The demo agent record once provisioned */
  demoAgent: Agent | null;
  /** Reset state */
  reset: () => void;
}

/**
 * Hook for starting a demo conversation with a genie persona.
 * Handles lazy provisioning - creates the ElevenLabs agent on demand if needed.
 */
export const useDemoConversation = (): UseDemoConversationReturn => {
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [provisionError, setProvisionError] = useState<string | null>(null);
  const [demoAgent, setDemoAgent] = useState<Agent | null>(null);
  const queryClient = useQueryClient();

  const startDemo = useCallback(
    async (personaId: string): Promise<string> => {
      setIsProvisioning(true);
      setProvisionError(null);

      try {
        const agent = await DemoGenieService.getOrProvisionDemoAgent(personaId);
        setDemoAgent(agent);

        // If agent exists but no elevenlabs_id yet, poll until ready
        if (!agent.elevenlabs_id) {
          const ready = await pollForReady(personaId);
          setDemoAgent(ready);
          return ready.elevenlabs_id!;
        }

        // Invalidate query cache so UI updates
        queryClient.invalidateQueries({ queryKey: ["demo-genies"] });

        return agent.elevenlabs_id;
      } catch (error) {
        const message = (error as Error).message || "Failed to start demo";
        setProvisionError(message);
        throw error;
      } finally {
        setIsProvisioning(false);
      }
    },
    [queryClient],
  );

  const reset = useCallback(() => {
    setIsProvisioning(false);
    setProvisionError(null);
    setDemoAgent(null);
  }, []);

  return {
    startDemo,
    isProvisioning,
    provisionError,
    demoAgent,
    reset,
  };
};

/**
 * Poll for a demo agent to become ready (max 30 seconds)
 */
async function pollForReady(personaId: string, maxAttempts = 15): Promise<Agent> {
  for (let i = 0; i < maxAttempts; i++) {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    const agent = await DemoGenieService.getDemoAgentByPersonaId(personaId);
    if (agent?.elevenlabs_id) {
      return agent;
    }
  }
  throw new Error("Demo provisioning timed out");
}
